import { useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, Search, Ban, CheckCircle2, Download, Upload, FileDown, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { fmtDate } from "../../lib/format";

const inputCls = "w-full border border-navy/20 bg-white px-4 py-3 text-sm text-navy focus:outline-none focus:border-rouge transition-colors duration-300";
const labelCls = "block font-heading uppercase tracking-wider text-xs text-navy/70 mb-1.5";

const FILTRES = [
  { value: "", label: "Tous" },
  { value: "adhesion_validee", label: "Adhésion validée" },
  { value: "paiement_en_attente", label: "Paiement en attente" },
  { value: "ffc", label: "FFC" },
  { value: "fsgt", label: "FSGT" },
  { value: "ffc_fsgt", label: "FFC + FSGT" },
  { value: "sans_licence", label: "Sans licence" },
  { value: "compte_cree", label: "Compte créé" },
  { value: "compte_non_cree", label: "Compte non créé" },
];

const EMPTY_FORM = {
  nom: "", prenom: "", date_naissance: "", adresse: "", code_postal: "", ville: "",
  telephone: "", email: "", saison: "", statut: "INSCRIPTION VALIDÉE",
  nip: "", uci_id: "", numero_licence: "", sexe: "", age_reglementaire: "",
  categorie: "", discipline: "", etat_licence: "",
  licence_ffc: false, licence_ffc_type: "", licence_fsgt: false,
  montant_total: "", paiement_statut: "", paiement_date: "", notes: "",
};

const Oui = () => <span className="text-green-700 font-bold text-xs">Oui</span>;
const Non = () => <span className="text-steel/60 text-xs">Non</span>;

async function downloadFile(url, fallbackName) {
  const r = await api.get(url, { responseType: "blob" });
  const dispo = r.headers["content-disposition"] || "";
  const m = dispo.match(/filename="?([^";]+)"?/);
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([r.data]));
  a.download = m ? m[1] : fallbackName;
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function AdminAdherents() {
  const [adherents, setAdherents] = useState([]);
  const [busy, setBusy] = useState(true);
  const [q, setQ] = useState("");
  const [filtre, setFiltre] = useState("");
  const [saison, setSaison] = useState("");
  const [fiche, setFiche] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);

  const load = async (search = q, f = filtre, s = saison) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/adherents?q=${encodeURIComponent(search)}&filtre=${f}&saison=${s}`);
      setAdherents(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load("", "", ""); }, []);

  const changeFiltre = (f) => { setFiltre(f); load(q, f, saison); };
  const changeSaison = (s) => { setSaison(s); load(q, filtre, s); };

  const openFiche = async (a) => {
    try {
      const { data } = await api.get(`/admin/adherents/${a.id}`);
      setFiche(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const startEdit = (a) => {
    const latest = (a.saisons || []).slice(-1)[0] || {};
    setEditing(a.id);
    setForm({
      nom: a.nom || "", prenom: a.prenom || "", date_naissance: a.date_naissance || "",
      adresse: a.adresse || "", code_postal: a.code_postal || "", ville: a.ville || "",
      telephone: a.telephone || "", email: a.email || "",
      nip: a.nip || "", uci_id: a.uci_id || "", numero_licence: a.numero_licence || "",
      sexe: a.sexe || "", age_reglementaire: a.age_reglementaire || "",
      categorie: a.categorie || "", discipline: a.discipline || "", etat_licence: a.etat_licence || "",
      saison: latest.saison || "", statut: latest.statut || "INSCRIPTION VALIDÉE",
      licence_ffc: Boolean(latest.licences?.ffc?.present), licence_ffc_type: latest.licences?.ffc?.type || "",
      licence_fsgt: Boolean(latest.licences?.fsgt?.present),
      montant_total: latest.paiement?.total || "", paiement_statut: latest.paiement?.statut || "",
      paiement_date: latest.paiement?.date || "", notes: a.notes || "",
    });
    setFiche(null);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/admin/adherents", form);
        toast.success("Adhérent ajouté");
      } else {
        await api.put(`/admin/adherents/${editing}`, form);
        toast.success("Adhérent mis à jour");
      }
      setEditing(null);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (a) => {
    if (a.active && !window.confirm(`Désactiver ${a.prenom} ${a.nom} ? Son compte Espace Licencié sera également désactivé.`)) return;
    try {
      const { data } = await api.patch(`/admin/adherents/${a.id}/desactiver`, { active: !a.active });
      toast.success(data.message);
      setFiche(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const supprimerCompte = async (a) => {
    if (!window.confirm(
      `Supprimer le compte de connexion de ${a.prenom} ${a.nom} (${a.email || "sans email"}) ?\n\n` +
      "La fiche du licencié et tout son historique sont CONSERVÉS — seul l'accès à l'Espace Licencié est supprimé. Il pourra recréer un compte plus tard."
    )) return;
    try {
      const { data } = await api.delete(`/admin/comptes/${a.account_id}`);
      toast.success(data.message);
      setFiche(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const runPreview = async () => {
    if (!importFile) { toast.error("Choisissez un fichier CSV ou Excel"); return; }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await api.post("/admin/adherents/import/preview", fd);
      setPreview(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setImporting(false);
    }
  };

  const confirmImport = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/admin/adherents/import/confirm", { preview_id: preview.preview_id });
      toast.success(data.message);
      setPreview(null);
      setImportFile(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setImporting(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  return (
    <main className="max-w-[1400px] mx-auto px-5 sm:px-8 py-10">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <p className="text-steel text-sm" data-testid="adherents-count">{adherents.length} adhérent{adherents.length > 1 ? "s" : ""}</p>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => downloadFile("/admin/adherents/modele", "modele-import-adherents.csv")} data-testid="adherents-template" className="inline-flex items-center gap-2 border border-navy/25 text-navy font-heading uppercase tracking-wider text-xs px-4 py-2.5 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300">
            <FileDown size={14} /> Télécharger le modèle d'import
          </button>
          <button onClick={() => downloadFile("/admin/adherents/export", "adherents.csv")} data-testid="adherents-export" className="inline-flex items-center gap-2 border border-navy/25 text-navy font-heading uppercase tracking-wider text-xs px-4 py-2.5 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300">
            <Download size={14} /> Export CSV
          </button>
          <button onClick={() => downloadFile("/admin/adherents/export?format=xlsx", "adherents.xlsx")} data-testid="adherents-export-xlsx" className="inline-flex items-center gap-2 border border-navy/25 text-navy font-heading uppercase tracking-wider text-xs px-4 py-2.5 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300">
            <Download size={14} /> Export Excel
          </button>
          <button onClick={() => { setEditing("new"); setForm(EMPTY_FORM); setFiche(null); }} data-testid="adherent-new" className="inline-flex items-center gap-2 bg-navy text-white font-heading uppercase tracking-wider text-xs px-4 py-2.5 hover:bg-rouge transition-colors duration-300">
            <Plus size={14} /> Ajouter un adhérent
          </button>
        </div>
      </div>

      <div className="bg-white border border-navy/10 p-5 mb-8" data-testid="adherents-import">
        <p className="font-heading uppercase tracking-wider text-xs text-navy/70 mb-3">Importer les adhérents (CSV ou Excel)</p>
        <div className="flex flex-wrap items-center gap-3">
          <label data-testid="import-file-label" className="inline-flex items-center gap-2 border border-dashed border-navy/25 px-5 py-3 cursor-pointer text-sm text-steel hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300">
            <Upload size={15} />
            {importFile ? importFile.name : "Choisir un fichier (.csv, .xlsx)"}
            <input type="file" data-testid="import-file" accept=".csv,.xlsx,.xlsm" className="hidden" onChange={(e) => { setImportFile(e.target.files?.[0] || null); setPreview(null); }} />
          </label>
          <button onClick={runPreview} disabled={importing} data-testid="import-preview-btn" className="inline-flex items-center gap-2 bg-navy text-white font-heading uppercase tracking-wider text-xs px-5 py-3 hover:bg-rouge disabled:opacity-60 transition-colors duration-300">
            {importing && <Loader2 size={14} className="animate-spin" />} Aperçu de l'import
          </button>
        </div>

        {preview && (
          <div className="mt-6 border-t border-navy/10 pt-6" data-testid="import-preview">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <div className="bg-mist p-4 text-center"><p className="font-heading font-semibold text-2xl text-navy" data-testid="preview-total">{preview.total}</p><p className="text-xs text-steel uppercase tracking-wider">Lignes</p></div>
              <div className="bg-green-50 p-4 text-center"><p className="font-heading font-semibold text-2xl text-green-700" data-testid="preview-nouveaux">{preview.nb_nouveaux}</p><p className="text-xs text-steel uppercase tracking-wider">Nouvelles fiches</p></div>
              <div className="bg-amber-50 p-4 text-center"><p className="font-heading font-semibold text-2xl text-amber-700" data-testid="preview-existants">{preview.nb_existants}</p><p className="text-xs text-steel uppercase tracking-wider">Déjà existants</p></div>
              <div className="bg-rouge/5 p-4 text-center"><p className="font-heading font-semibold text-2xl text-rouge" data-testid="preview-erreurs">{preview.nb_erreurs}</p><p className="text-xs text-steel uppercase tracking-wider">Erreurs</p></div>
            </div>
            {preview.nb_sans_email > 0 && (
              <p className="mb-4 bg-amber-50 border border-amber-300 text-amber-800 text-xs px-4 py-3" data-testid="preview-sans-email">
                {preview.nb_sans_email} licencié(s) sans adresse email : ils seront marqués « EMAIL MANQUANT » et pourront créer leur accès avec leur numéro de licence.
              </p>
            )}
            {preview.erreurs.length > 0 && (
              <div className="mb-4 max-h-40 overflow-y-auto" data-testid="preview-erreurs-list">
                {preview.erreurs.map((e, i) => (
                  <p key={i} className="text-xs text-rouge py-1">Ligne {e.ligne} — {e.email || "(sans email)"} : {e.raison}</p>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-3">
              <button onClick={confirmImport} disabled={importing || preview.nb_nouveaux === 0} data-testid="import-confirm" className="inline-flex items-center gap-2 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-7 py-3.5 hover:bg-navy disabled:opacity-60 transition-colors duration-300">
                {importing && <Loader2 size={15} className="animate-spin" />} Confirmer l'import
              </button>
              <button onClick={() => { setPreview(null); setImportFile(null); }} data-testid="import-cancel" className="border border-navy/25 text-navy font-heading uppercase tracking-wider text-sm px-7 py-3.5 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300">
                Annuler
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="relative">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-steel" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="adherents-search" placeholder="Nom, email, n° licence, NIP, UCI ID…" className="border border-navy/20 bg-white pl-10 pr-4 py-2.5 text-sm text-navy focus:outline-none focus:border-rouge w-72" />
        </form>
        {[{ value: "", label: "Toutes saisons" }, { value: "2026", label: "Adhérents 2026" }, { value: "2027", label: "Adhérents 2027" }].map((s) => (
          <button key={s.value} onClick={() => changeSaison(s.value)} data-testid={`adherents-saison-${s.value || "toutes"}`} className={`font-heading uppercase tracking-wider text-[11px] px-3.5 py-2 border transition-[background-color,color,border-color] duration-300 ${saison === s.value ? "bg-rouge border-rouge text-white" : "border-navy/20 text-navy/70 hover:border-rouge hover:text-rouge"}`}>
            {s.label}
          </button>
        ))}
        <span className="w-px h-6 bg-navy/15 hidden sm:block" />
        {FILTRES.map((f) => (
          <button key={f.value} onClick={() => changeFiltre(f.value)} data-testid={`adherents-filtre-${f.value || "tous"}`} className={`font-heading uppercase tracking-wider text-[11px] px-3.5 py-2 border transition-[background-color,color,border-color] duration-300 ${filtre === f.value ? "bg-navy border-navy text-white" : "border-navy/20 text-navy/70 hover:border-rouge hover:text-rouge"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {busy ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-rouge" size={28} /></div>
      ) : adherents.length === 0 ? (
        <div className="border border-dashed border-navy/25 px-8 py-16 text-center" data-testid="adherents-empty">
          <p className="font-heading uppercase font-semibold tracking-tight text-navy text-2xl">Base vide</p>
          <p className="mt-2 text-sm text-steel">Importez le fichier des adhérents existants ou attendez les prochaines inscriptions en ligne.</p>
        </div>
      ) : (
        <div className="bg-white border border-navy/10 overflow-x-auto" data-testid="adherents-table">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="bg-navy text-white">
                {["Nom", "Prénom", "N° Licence", "Catégorie", "Email", "Saison", "Adhésion", "FFC", "FSGT", "Paiement", "Compte"].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-heading uppercase tracking-wider font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {adherents.map((a) => (
                <tr key={a.id} onClick={() => openFiche(a)} data-testid={`adherent-row-${a.id.slice(0, 8)}`} className={`border-b border-navy/10 last:border-0 cursor-pointer transition-colors hover:bg-mist ${!a.active ? "opacity-50" : ""}`}>
                  <td className="px-4 py-3.5 text-sm font-semibold text-navy">{a.nom}</td>
                  <td className="px-4 py-3.5 text-sm text-navy">{a.prenom}</td>
                  <td className="px-4 py-3.5 text-sm text-steel">{a.numero_licence || "—"}</td>
                  <td className="px-4 py-3.5 text-sm text-steel">{a.categorie || "—"}</td>
                  <td className="px-4 py-3.5 text-sm text-steel">
                    {a.email_manquant ? (
                      <span className="inline-block bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider px-2 py-1" data-testid={`email-manquant-${a.id.slice(0, 8)}`}>Email manquant</span>
                    ) : a.email}
                  </td>
                  <td className="px-4 py-3.5 text-sm text-navy">{a.saison}</td>
                  <td className="px-4 py-3.5"><span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 ${a.statut === "INSCRIPTION VALIDÉE" ? "bg-green-100 text-green-700" : a.statut === "ADHÉSION EXPIRÉE" ? "bg-rouge/10 text-rouge" : "bg-steel/15 text-steel"}`}>{a.statut || "—"}</span></td>
                  <td className="px-4 py-3.5">{a.ffc ? <Oui /> : <Non />}</td>
                  <td className="px-4 py-3.5">{a.fsgt ? <Oui /> : <Non />}</td>
                  <td className="px-4 py-3.5 text-sm text-navy">{a.paiement || "—"}</td>
                  <td className="px-4 py-3.5" data-testid={`adherent-compte-${a.id.slice(0, 8)}`}>{a.compte_cree ? <Oui /> : <Non />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {fiche && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="adherent-fiche">
          <div className="absolute inset-0 bg-navy/70" onClick={() => setFiche(null)} />
          <div className="relative bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 sm:p-10">
            <button onClick={() => setFiche(null)} data-testid="fiche-close" className="absolute top-4 right-4 p-2 text-navy/50 hover:text-rouge transition-colors" aria-label="Fermer"><X size={20} /></button>
            <p className="font-heading uppercase tracking-wider text-[11px] text-rouge mb-1">{fiche.numero_membre}</p>
            <h3 className="font-heading uppercase font-semibold tracking-tight text-navy text-3xl mb-6">{fiche.prenom} {fiche.nom}</h3>
            <div className="grid sm:grid-cols-2 gap-x-8 text-sm">
              {[["Email", fiche.email || ""], ["Téléphone", fiche.telephone], ["Date de naissance", fiche.date_naissance],
                ["Numéro de licence", fiche.numero_licence], ["NIP", fiche.nip], ["UCI ID", fiche.uci_id],
                ["Catégorie", fiche.categorie], ["Discipline", fiche.discipline], ["État de licence", fiche.etat_licence],
                ["Sexe", fiche.sexe], ["Âge réglementaire", fiche.age_reglementaire],
                ["Adresse", `${fiche.adresse || ""} ${fiche.code_postal || ""} ${fiche.ville || ""}`.trim()],
                ["Compte créé", fiche.compte?.cree ? `Oui (${fmtDate(fiche.compte.date_creation)})` : "Non"],
                ["Dernière connexion", fiche.compte?.derniere_connexion ? fmtDate(fiche.compte.derniere_connexion) : "—"]].map(([l, v]) => (
                <div key={l} className="py-2.5 border-b border-navy/10"><p className="font-heading uppercase tracking-wider text-[10px] text-steel">{l}</p><p className="text-navy mt-0.5">{v || "—"}</p></div>
              ))}
            </div>
            {!fiche.email && (
              <p className="mt-4 bg-amber-50 border border-amber-300 text-amber-800 text-xs px-4 py-3" data-testid="fiche-email-manquant">
                EMAIL MANQUANT — ajoutez l'adresse email du licencié via « Modifier » pour qu'il puisse créer son accès avec son email.
                Il peut aussi créer son accès seul avec son numéro de licence depuis « Première connexion ».
              </p>
            )}
            {(fiche.saisons || []).map((s) => (
              <div key={s.saison} className="mt-6 bg-mist border-l-2 border-rouge p-5" data-testid={`fiche-saison-${s.saison}`}>
                <p className="font-heading uppercase font-semibold text-navy mb-2">Saison {s.saison} — {s.statut}</p>
                <p className="text-xs text-steel">
                  FFC : {s.licences?.ffc?.present ? `Oui${s.licences.ffc.type ? ` (${s.licences.ffc.type})` : ""}` : "Non"} ·
                  FSGT : {s.licences?.fsgt?.present ? "Oui" : "Non"} ·
                  Total : {s.paiement?.total || "—"} · Paiement : {s.paiement?.statut || "—"}
                  {s.licence_details?.numero_licence ? ` · Licence n° ${s.licence_details.numero_licence}${s.licence_details.etat ? ` (${s.licence_details.etat})` : ""}` : ""}
                  {s.licence_details?.categories?.length ? ` · ${s.licence_details.categories.map((c) => [c.categorie, c.sous_categorie, c.discipline].filter(Boolean).join(" ")).join(", ")}` : ""}
                  {s.equipements?.length ? ` · Équipements : ${s.equipements.map((e) => `${e.label} ${e.taille} ×${e.quantite}`).join(", ")}` : ""}
                </p>
              </div>
            ))}
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={() => startEdit(fiche)} data-testid="fiche-edit" className="inline-flex items-center gap-2 bg-rouge text-white font-heading uppercase tracking-wider text-xs px-5 py-3 hover:bg-navy transition-colors duration-300">
                <Pencil size={14} /> Modifier
              </button>
              <button onClick={() => toggle(fiche)} data-testid="fiche-toggle" className="inline-flex items-center gap-2 border border-navy/25 text-navy font-heading uppercase tracking-wider text-xs px-5 py-3 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300">
                {fiche.active ? <><Ban size={14} /> Désactiver</> : <><CheckCircle2 size={14} /> Réactiver</>}
              </button>
              {fiche.account_id && (
                <button onClick={() => supprimerCompte(fiche)} data-testid="fiche-supprimer-compte" className="inline-flex items-center gap-2 border border-rouge/40 text-rouge font-heading uppercase tracking-wider text-xs px-5 py-3 hover:bg-rouge hover:text-white transition-colors duration-300">
                  <Trash2 size={14} /> Supprimer le compte de connexion
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="adherent-editor">
          <div className="absolute inset-0 bg-navy/70" onClick={() => setEditing(null)} />
          <form onSubmit={save} className="relative bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6 sm:p-10">
            <h3 className="font-heading uppercase font-semibold tracking-tight text-navy text-2xl mb-6">
              {editing === "new" ? "Ajouter un adhérent" : "Modifier l'adhérent"}
            </h3>
            <div className="grid sm:grid-cols-2 gap-5">
              <div><label className={labelCls}>Nom *</label><input data-testid="adh-field-nom" required value={form.nom} onChange={set("nom")} className={inputCls} /></div>
              <div><label className={labelCls}>Prénom *</label><input data-testid="adh-field-prenom" required value={form.prenom} onChange={set("prenom")} className={inputCls} /></div>
              <div><label className={labelCls}>Email</label><input data-testid="adh-field-email" type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="Laisser vide si inconnu" /></div>
              <div><label className={labelCls}>Téléphone</label><input data-testid="adh-field-telephone" value={form.telephone} onChange={set("telephone")} className={inputCls} /></div>
              <div><label className={labelCls}>Date de naissance</label><input data-testid="adh-field-naissance" type="date" value={form.date_naissance} onChange={set("date_naissance")} className={inputCls} /></div>
              <div><label className={labelCls}>Saison</label><input data-testid="adh-field-saison" value={form.saison} onChange={set("saison")} className={inputCls} placeholder="2026" /></div>
              <div><label className={labelCls}>N° de licence</label><input data-testid="adh-field-licence" value={form.numero_licence} onChange={set("numero_licence")} className={inputCls} /></div>
              <div><label className={labelCls}>NIP</label><input data-testid="adh-field-nip" value={form.nip} onChange={set("nip")} className={inputCls} /></div>
              <div><label className={labelCls}>UCI ID</label><input data-testid="adh-field-uci" value={form.uci_id} onChange={set("uci_id")} className={inputCls} /></div>
              <div>
                <label className={labelCls}>Sexe</label>
                <select data-testid="adh-field-sexe" value={form.sexe} onChange={set("sexe")} className={inputCls}>
                  <option value="">—</option>
                  <option value="H">H</option>
                  <option value="F">F</option>
                </select>
              </div>
              <div><label className={labelCls}>Âge réglementaire</label><input data-testid="adh-field-age" value={form.age_reglementaire} onChange={set("age_reglementaire")} className={inputCls} /></div>
              <div><label className={labelCls}>Catégorie</label><input data-testid="adh-field-categorie" value={form.categorie} onChange={set("categorie")} className={inputCls} placeholder="Open, U17…" /></div>
              <div><label className={labelCls}>Discipline</label><input data-testid="adh-field-discipline" value={form.discipline} onChange={set("discipline")} className={inputCls} placeholder="Route, VTT…" /></div>
              <div><label className={labelCls}>État de licence</label><input data-testid="adh-field-etat-licence" value={form.etat_licence} onChange={set("etat_licence")} className={inputCls} placeholder="Licence valide" /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Adresse</label><input data-testid="adh-field-adresse" value={form.adresse} onChange={set("adresse")} className={inputCls} /></div>
              <div><label className={labelCls}>Code postal</label><input data-testid="adh-field-cp" value={form.code_postal} onChange={set("code_postal")} className={inputCls} /></div>
              <div><label className={labelCls}>Ville</label><input data-testid="adh-field-ville" value={form.ville} onChange={set("ville")} className={inputCls} /></div>
              <div>
                <label className={labelCls}>Statut de l'adhésion</label>
                <select data-testid="adh-field-statut" value={form.statut} onChange={set("statut")} className={inputCls}>
                  {["INSCRIPTION EN COURS", "PAIEMENT EN ATTENTE", "INSCRIPTION VALIDÉE", "DOSSIER À VÉRIFIER", "ADHÉSION EXPIRÉE"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="flex items-end gap-6 pb-1">
                <label className="flex items-center gap-2 text-sm text-navy cursor-pointer"><input type="checkbox" data-testid="adh-field-ffc" checked={form.licence_ffc} onChange={set("licence_ffc")} className="w-4 h-4 accent-[#D90429]" /> Licence FFC</label>
                <label className="flex items-center gap-2 text-sm text-navy cursor-pointer"><input type="checkbox" data-testid="adh-field-fsgt" checked={form.licence_fsgt} onChange={set("licence_fsgt")} className="w-4 h-4 accent-[#D90429]" /> Licence FSGT</label>
              </div>
              {form.licence_ffc && (
                <div className="sm:col-span-2"><label className={labelCls}>Type de licence FFC</label><input data-testid="adh-field-ffc-type" value={form.licence_ffc_type} onChange={set("licence_ffc_type")} className={inputCls} placeholder="Compétition, Open…" /></div>
              )}
              <div><label className={labelCls}>Montant total</label><input data-testid="adh-field-total" value={form.montant_total} onChange={set("montant_total")} className={inputCls} placeholder="240,00 €" /></div>
              <div><label className={labelCls}>Statut du paiement</label><input data-testid="adh-field-paiement" value={form.paiement_statut} onChange={set("paiement_statut")} className={inputCls} placeholder="Payé" /></div>
              <div><label className={labelCls}>Date du paiement</label><input data-testid="adh-field-date-paiement" type="date" value={form.paiement_date} onChange={set("paiement_date")} className={inputCls} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Notes internes</label><textarea data-testid="adh-field-notes" rows={2} value={form.notes} onChange={set("notes")} className={inputCls} /></div>
            </div>
            <div className="flex gap-3 mt-8">
              <button type="submit" disabled={saving} data-testid="adherent-save" className="inline-flex items-center gap-2 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-7 py-3.5 hover:bg-navy disabled:opacity-60 transition-colors duration-300">
                {saving && <Loader2 size={15} className="animate-spin" />} Enregistrer
              </button>
              <button type="button" onClick={() => setEditing(null)} data-testid="adherent-cancel" className="border border-navy/25 text-navy font-heading uppercase tracking-wider text-sm px-7 py-3.5 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300">
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}
    </main>
  );
}
